import type { AuthUser, UpdateAccountBody } from '@viewer/protocol';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorText } from '../../lib/api/client';
import styles from './AccountTab.module.css';

interface Props {
  user: AuthUser;
  onUpdated(user: AuthUser): void;
}

export function AccountTab({ user, onUpdated }: Props) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(user.name);
    setEmail(user.email);
  }, [user.id, user.name, user.email]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (newPassword) {
      if (newPassword.length < 8) return setError('A nova senha precisa ter pelo menos 8 caracteres.');
      if (newPassword !== confirmPassword) return setError('As senhas novas não conferem.');
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const body: UpdateAccountBody = {
        name: name.trim(),
        email: email.trim(),
        ...(currentPassword ? { currentPassword } : {}),
        ...(newPassword ? { newPassword } : {}),
      };
      const updated = await api.patch<AuthUser>('/api/me', body);
      onUpdated(updated);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setSaved(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className={styles.panel}>
      <form className={styles.form} onSubmit={(e) => void submit(e)}>
        <label className={styles.field}>
          <span className={styles.label}>Nome</span>
          <input
            className={styles.input}
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={80}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>E-mail</span>
          <input
            className={styles.input}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Senha atual</span>
          <input
            className={styles.input}
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            placeholder="Obrigatória para mudar e-mail ou senha"
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Nova senha</span>
          <input
            className={styles.input}
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Deixe em branco para manter"
            minLength={8}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>Confirmar nova senha</span>
          <input
            className={styles.input}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Repita a nova senha"
            minLength={8}
          />
        </label>
        {error && <p className={styles.error}>{error}</p>}
        {saved && !error && <p className={styles.ok}>Dados da conta atualizados.</p>}
        <button type="submit" className={styles.primary} disabled={busy}>
          {busy ? 'Salvando...' : 'Salvar'}
        </button>
      </form>
    </section>
  );
}
